import React from "react";
import { motion } from "framer-motion";
import {
  ComposedChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { aiAssuranceData, aiModelHealth, humanReviewQueue } from "../data/mockData";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, STATUS_KEY, statusClasses, statusHex, thresholdStatus } from "../data/statusColors";
import { ShieldCheck, Clock } from "lucide-react";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveAssuranceSection } from "../components/live/LiveGovernanceSections";
import { StaticContentDivider } from "../components/live/primitives";

const statusBadge = s =>
  s === "Healthy" ? "badge badge-success" :
  s === "Monitoring" ? "badge badge-warning" : "badge badge-critical";

const reviewStatusBadge = s =>
  s === "Approved"  ? "badge badge-success" :
  s === "Rejected"  ? "badge badge-critical" :
  s === "Reviewing" ? "badge badge-info" : "badge badge-warning";

// Human Review Queue confidence score -> STATUS bucket via the shared
// numeric-threshold resolver (bands sorted descending by min, `>=` match —
// mirrors the original >=80 / >=65 / else ternary exactly).
const CONFIDENCE_BANDS = [
  { min: 80, bucket: STATUS_KEY.good },
  { min: 65, bucket: STATUS_KEY.warning },
  { min: 0,  bucket: STATUS_KEY.critical },
];

export default function AIAssurance() {
  const t = useTranslate();
  const { assurance, accuracy } = useIntelligence({ running: false });

  return (
    <div className="space-y-8">
      <SectionHeader icon="Brain" title="AI Stack Assurance Layer"
        subtitle="AI confidence scoring · false-positive analytics · human review queue · model health monitoring · drift detection · explainability engine"
      />

      {/* ── The live assurance engine (§16) ──────────────────────────────
          This replaces four hardcoded tiles that used to sit here, one of
          which read "False Positive Rate 5.8%". That figure was invented,
          and the engine below states in terms that it cannot be computed:
          there is no labelled ground-truth incident set, because Simhastha
          2027 has not happened.

          A product that displays a fabricated precision figure on the page
          whose whole purpose is trustworthiness has undermined itself more
          than any missing feature could. The honest null is the feature. */}
      <LiveAssuranceSection assurance={assurance} accuracy={accuracy} />

      <StaticContentDivider />

      {/* Governance callout */}
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
        className="rounded-2xl bg-gradient-to-r from-violet-50 via-indigo-50 to-blue-50 border border-violet-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={22} className="text-violet-700" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-900 mb-1">{t("AI-Assisted Governance — Not Autonomous Governance")}</h3>
            <p className="text-xs text-slate-600 leading-relaxed max-w-3xl">
              {t("Every Maha Kumbh AI alert passes through a 3-stage validation pipeline:")}
              <strong className="text-slate-800"> {t("(1) Model Confidence Score")}</strong> →
              <strong className="text-slate-800"> {t("(2) Duplicate / False-positive Detection")}</strong> →
              <strong className="text-slate-800"> {t("(3) Human Review & Approval")}</strong>.
              {t("No enforcement action is taken from AI output alone. Human-in-loop is mandatory for all HIGH and CRITICAL alerts.")}
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {["Human-in-Loop Mandatory","Explainability Engine Active","No Autonomous Enforcement",
                "DPDP 2023 Aligned","Bias Monitoring","Audit Trail Active"].map(tag => (
                <span key={tag} className="badge badge-purple text-[10px]">{t(tag)}</span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Gauges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GaugeCard title="Model Accuracy"   value={93.7} index={0} />
        <GaugeCard title="Human Validation" value={86.9} index={1} />
        <GaugeCard title="Confidence Score" value={91.2} index={2} />
        <GaugeCard title="FP Filter (inv.)" value={94.2} index={3} />
      </div>

      {/* Timeline chart */}
      <ChartCard title="Alert Validation Timeline — Today"
        subtitle="Hourly: total AI alerts · validated · false positives · duplicates suppressed" live>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={aiAssuranceData.timeline} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="aaValGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(1)} stopOpacity={0.2} />
                <stop offset="100%" stopColor={chartColor(1)} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="aaTotGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.15} />
                <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="hour" tick={axisTick} />
            <YAxis tick={axisTick} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Area type="monotone" dataKey="total"     fill="url(#aaTotGrad)" stroke={chartColor(0)} strokeWidth={2} name={t("Total Alerts")} />
            <Area type="monotone" dataKey="validated" fill="url(#aaValGrad)" stroke={chartColor(1)} strokeWidth={2.5} name={t("Validated")} />
            <Bar dataKey="fp"  fill={chartColor(3)} radius={[3,3,0,0]} name={t("False Positive")} />
            <Bar dataKey="dup" fill={chartColor(7)} radius={[3,3,0,0]} name={t("Duplicates")} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Model health table */}
      <ChartCard title="AI Model Health Dashboard"
        subtitle="Per-model accuracy, data drift, bias level and status">
        <div className="overflow-x-auto">
          <table className="gov-table">
            <thead>
              <tr>
                <th>{t("Model Name")}</th><th>{t("Accuracy")}</th><th>{t("Drift Score")}</th>
                <th>{t("Bias Level")}</th><th>{t("Reviews")}</th><th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {aiModelHealth.map((m, i) => (
                <motion.tr key={m.model} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-bold text-slate-900">{t(m.model)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-1.5 bg-emerald-500 rounded-full" style={{ width:`${m.accuracy}%` }} />
                      </div>
                      <span className="font-bold text-xs">{m.accuracy}%</span>
                    </div>
                  </td>
                  <td><span className={`text-xs font-bold ${m.drift > 1.5 ? STATUS.warning.text : STATUS.good.text}`}>{m.drift}%</span></td>
                  <td><span className={`badge ${m.bias === "Low" ? "badge-success" : m.bias === "Medium" ? "badge-warning" : "badge-critical"}`}>{t(m.bias)}</span></td>
                  <td className="font-semibold">{m.reviewed.toLocaleString()}</td>
                  <td><span className={statusBadge(m.status)}>{t(m.status)}</span></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Human review queue */}
      <ChartCard title="Human Review Queue" subtitle="AI alerts requiring human validation — confidence threshold &lt;85%" live>
        <div className="space-y-3">
          {humanReviewQueue.map((item, i) => (
            <motion.div key={item.id}
              initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
              className="flex items-start gap-4 p-3 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-card-hover transition-all cursor-pointer"
            >
              <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center text-xs font-black ${statusClasses(thresholdStatus(item.confidence, CONFIDENCE_BANDS))}`}>
                {item.confidence}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-bold text-slate-800 leading-snug">{t(item.alert)}</p>
                  <span className={reviewStatusBadge(item.status)}>{t(item.status)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                  <span className="font-semibold text-violet-600">{t(item.model)}</span>
                  <span>· {item.assignee}</span>
                  <span className="flex items-center gap-1"><Clock size={9} />{item.time}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          {[["31","Pending",STATUS_KEY.neutral],["248","Approved",STATUS_KEY.good],["18","Rejected",STATUS_KEY.critical],["5.8%","FP Rate",STATUS_KEY.warning]].map(([v,l,bucket]) => (
            <div key={l}>
              <div className="text-lg font-black" style={{ color: statusHex(bucket) }}>{v}</div>
              <div className="text-[10px] text-slate-500">{t(l)}</div>
            </div>
          ))}
        </div>
      </ChartCard>

      {/* Source traceability */}
      <ChartCard title="Alert Source Traceability" subtitle="Origin of AI-generated alerts by sensor / data source type">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart layout="vertical"
            data={[
              { source:"CCTV Camera Network",      alerts:142, validated:131 },
              { source:"Drone Thermal Imagery",     alerts:84,  validated:78  },
              { source:"Crowd Density Sensor Grid", alerts:96,  validated:89  },
              { source:"Police Mobile Reports",     alerts:58,  validated:56  },
              { source:"Health Wearable Data",      alerts:34,  validated:31  },
              { source:"Transport IOT Sensors",     alerts:62,  validated:59  },
            ]}
            margin={{ left:10, right:20 }}
          >
            <XAxis type="number" tick={axisTick} />
            <YAxis type="category" dataKey="source" tick={axisTick} tickFormatter={v => t(v)} width={165} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Bar dataKey="alerts"    fill={chartColor(0)} radius={[0,3,3,0]} name={t("Total Alerts")} />
            <Bar dataKey="validated" fill={chartColor(1)} radius={[0,3,3,0]} name={t("Validated")}    />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
